/**
 *  @flow
 */

import React, { useEffect } from 'react';
import gql from 'graphql-tag';
import { useForm, FormProvider } from 'react-hook-form';
import { useMutation } from '@apollo/client';
import { useSelector } from 'react-redux';
import { injectIntl, defineMessages, FormattedMessage } from 'react-intl';
import { format, addMonths, startOfMonth } from 'date-fns';
import { map, get } from 'lodash';
import { Icon, Message } from 'semantic-ui-react';

import { Modal, Button } from '../../../components';
import CreateAppointmentForm from './form/CreateAppointmentForm';
import createFormValidate from '../containers/form/create-form-validate';
import { convertMinutesToDateTime } from '../../../lib/date';
import apolloClient from '../../../apollo-client';

import { GET_SALON_QUERY } from '../containers/timetable/MasterTable';
import { GET_WORKING_DAYS } from '../../master-appointments/containers/DateRange';
import { GET_MASTER_APPOINTMENTS_QUERY } from '../../master-appointments/containers/queries';

const intlMessages = defineMessages({
    timeOverlapError: {
        id: 'pages.schedule.form.AppointmentForm.startTimeError',
        defaultMessage: 'Time is overlapping with another appointment',
    },
    appointmentAlreadyExists: {
        id: 'pages.appointments.AppointmentForm.error.appointmentAlreadyExists',
        defaultMessage: 'Same appointment already exists',
    },
    defaultErrorMessage: {
        id: 'errors.defaultMessage',
        defaultMessage: 'Something went wrong',
    },
});

export const CREATE_APPOINTMENTS_QUERY = gql`
    mutation createAppointments(
        $input: CreateAppointmentsInput!
        $lang: String!
    ) {
        createAppointments(input: $input) {
            appointments {
                id
                author {
                    id
                    firstName
                    lastName
                }
                authorRole
                cabinet {
                    id
                    name(lang: $lang)
                }
                master {
                    id
                    firstName
                    lastName
                }
                client {
                    id
                    firstName
                    lastName
                    stats {
                        totalAppointments
                        failedAppointments
                        successAppointments
                        lastAppointment {
                            id
                            service {
                                id
                                name(lang: $lang)
                            }
                            startAt
                        }
                    }
                }
                service {
                    id
                    name(lang: $lang)
                    color
                    price
                }
                startAt
                endAt
                duration
                notes
                invoice {
                    id
                    paid
                }
                archived
            }
        }
    }
`;

const updateMasterAppointments = (
    store,
    master,
    salon,
    appointments,
    fromDate,
    toDate,
    showArchive,
    currentLanguage,
) => {
    try {
        const data = store.readQuery({
            query: GET_MASTER_APPOINTMENTS_QUERY,
            variables: {
                master,
                salon,
                fromDate: format(new Date(fromDate), 'yyyy-MM-dd'),
                toDate: format(new Date(toDate), 'yyyy-MM-dd'),
                withMaster: true,
                showArchive,
                lang: currentLanguage,
            },
        });

        if (
            data &&
            data.viewer &&
            data.viewer.salon &&
            data.viewer.salon.master &&
            data.viewer.salon.master.appointments
        ) {
            const newAppointments = appointments.map(appointment => ({
                ...appointment,
                invoice: null,
            }));

            store.writeQuery({
                query: GET_MASTER_APPOINTMENTS_QUERY,
                variables: {
                    master,
                    salon,
                    fromDate: format(new Date(fromDate), 'yyyy-MM-dd'),
                    toDate: format(new Date(toDate), 'yyyy-MM-dd'),
                    withMaster: true,
                    showArchive,
                    lang: currentLanguage,
                },
                data: {
                    ...data,
                    viewer: {
                        ...data.viewer,
                        salon: {
                            ...data.viewer.salon,
                            master: {
                                ...data.viewer.salon.master,
                                appointments: [
                                    ...data.viewer.salon.master.appointments,
                                    ...newAppointments,
                                ],
                            },
                        },
                    },
                },
            });
        }
    } catch (e) {
        // cache not populated yet — safe to ignore
    }
};

const updateWorkingDays = (store, salon, master, appointments) => {
    const fromDate = format(new Date(), 'yyyy-MM-dd');
    const toDate = format(addMonths(startOfMonth(new Date()), 6), 'yyyy-MM-dd');
    try {
        const data = store.readQuery({
            query: GET_WORKING_DAYS,
            variables: {
                salon,
                master,
                fromDate,
                toDate,
            },
        });

        if (
            data &&
            data.viewer &&
            data.viewer.salon &&
            data.viewer.salon.workingDays
        ) {
            let currentWorkingDays = [...data.viewer.salon.workingDays];

            appointments.forEach(appointment => {
                const targetDateStr = format(new Date(appointment.startAt), 'yyyy-MM-dd');
                const index = currentWorkingDays.findIndex(
                    item => item.date === targetDateStr,
                );
                if (index !== -1) {
                    currentWorkingDays[index] = {
                        ...currentWorkingDays[index],
                        appointmentCount:
                            currentWorkingDays[index].appointmentCount + 1,
                    };
                } else {
                    currentWorkingDays.push({
                        date: targetDateStr,
                        appointmentCount: 1,
                        __typename: 'WorkingDay',
                    });
                }
            });

            store.writeQuery({
                query: GET_WORKING_DAYS,
                variables: {
                    salon,
                    master,
                    fromDate,
                    toDate,
                },
                data: {
                    ...data,
                    viewer: {
                        ...data.viewer,
                        salon: {
                            ...data.viewer.salon,
                            workingDays: currentWorkingDays,
                        },
                    },
                },
            });
        }
    } catch (e) {
        // cache not populated yet — safe to ignore
    }
};

const ErrorMessage = ({ error }) => (
    <Message error>
        <Icon name="warning sign" />
        {error}
    </Message>
);

function CreateAppointmentDialog({
    trigger,
    open,
    onClose,
    time,
    date: dateProp,
    id,
    inCabinet,
    additionalTime,
    masterAppointments,
    fromDate,
    toDate,
    createAppointments: createAppointmentsProp,
    intl,
}) {
    const salonId = useSelector(({ user }) => user.get('salon'));
    const userId = useSelector(({ user }) => user.get('id'));
    const showArchive = useSelector(({ salon: s }) => s.get('showArchive'));
    const currentLanguage = useSelector(
        ({ intl: i }) => i.get('locale') || i.get('defaultLanguage'),
    );
    const reduxDate = useSelector(({ appointments }) => appointments.get('date'));
    const date = dateProp || reduxDate;

    // Read salon object from Apollo local cache (same as withSalon HOC)
    let salonObj = null;
    try {
        const cacheData = apolloClient.readQuery({
            query: GET_SALON_QUERY,
            variables: {
                salon: salonId,
                showArchive,
                date: format(new Date(date), 'yyyy-MM-dd'),
                lang: currentLanguage,
            },
        });
        salonObj = (cacheData && cacheData.viewer && cacheData.viewer.salon) || null;
    } catch (e) {
        // cache not populated yet
    }

    const defaultMaster = !inCabinet ? id : undefined;
    const defaultCabinet = inCabinet ? id : undefined;

    const getInitialValues = () => {
        const service = {};
        if (typeof time === 'number') {
            service.startTime = time + (additionalTime || 0);
        }
        if (id) {
            if (inCabinet) {
                service.cabinet = id;
            } else {
                service.master = id;
            }
        }
        return { startDate: date, services: [service] };
    };

    const scheduleEndAt = useSelector(({ salon: s }) =>
        get(s.get('settings'), 'endAt'),
    );

    const methods = useForm({
        defaultValues: getInitialValues(),
        resolver: createFormValidate,
        context: { intl, inCabinet, scheduleEndAt },
    });
    const {
        handleSubmit,
        reset,
        setError,
        formState: { isSubmitting, errors },
    } = methods;

    // Re-initialize form when dialog opens
    useEffect(() => {
        if (open) {
            reset(getInitialValues());
        }
    }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

    const [createAppointmentsMutation] = useMutation(CREATE_APPOINTMENTS_QUERY);

    const onSubmit = async formData => {
        try {
        const appointments = map(formData.services, service => ({
            salon: salonId,
            cabinet: service.cabinet,
            master: service.master,
            client: formData.client,
            service: service.service,
            duration: service.duration,
            notes: formData.notes,
            startAt: format(
                convertMinutesToDateTime(service.startTime, formData.startDate),
                'yyyy-MM-dd HH:mm',
            ),
            author: userId,
            authorRole: 'ADMIN',
        }));

        await createAppointmentsMutation({
                variables: {
                    input: { appointments },
                    lang: currentLanguage,
                },
                update: (store, { data: { createAppointments } }) => {
                    if (masterAppointments) {
                        const filteredAppts = createAppointments.appointments.filter(
                            a => a.master.id === id,
                        );
                        updateMasterAppointments(
                            store,
                            id,
                            salonId,
                            filteredAppts,
                            fromDate,
                            toDate,
                            showArchive,
                            currentLanguage,
                        );
                        updateWorkingDays(store, salonId, id, filteredAppts);
                    } else {
                        if (createAppointmentsProp) {
                            createAppointmentsProp(createAppointments.appointments);
                        }
                    }
                },
            });
            onClose();
        } catch (error) {
            const graphQLError = error.graphQLErrors && error.graphQLErrors[0];
            if (graphQLError) {
                const errorMsg = get(graphQLError, 'data.error');
                if (
                    graphQLError.name === 'AlreadyExists' &&
                    errorMsg === 'TIME_OVERLAPS_WITH_OTHER_RECORD'
                ) {
                    setError('services.0.startTime', {
                        message: intl.formatMessage(intlMessages.timeOverlapError),
                    });
                    return;
                }
                if (errorMsg === 'APPOINTMENT_ALREADY_EXISTS') {
                    setError('root', {
                        message: intl.formatMessage(intlMessages.appointmentAlreadyExists),
                    });
                    return;
                }
            }
            setError('root', {
                message: intl.formatMessage(intlMessages.defaultErrorMessage),
            });
        }
    };

    return (
        <FormProvider {...methods}>
            <Modal size="large" trigger={trigger} onClose={onClose} open={open}>
                <Modal.Header>
                    <FormattedMessage
                        id="pages.schedule.CreateAppointmentDialog.newAppointment"
                        defaultMessage="New Appointment"
                    />
                </Modal.Header>
                <Modal.Content>
                    {errors.root && <ErrorMessage error={errors.root.message} />}
                    <CreateAppointmentForm
                        onCloseDialog={onClose}
                        defaultMaster={defaultMaster}
                        defaultCabinet={defaultCabinet}
                        inCabinet={inCabinet}
                        salon={salonObj}
                    />
                </Modal.Content>
                <Modal.Actions>
                    <Button
                        onClick={handleSubmit(onSubmit)}
                        disabled={isSubmitting}
                        loading={isSubmitting}
                        primary
                    >
                        <FormattedMessage
                            id="pages.schedule.CreateAppointmentDialog.createAppointment"
                            defaultMessage="Create Appointment"
                        />
                    </Button>
                    <Button onClick={onClose} disabled={isSubmitting} basic>
                        <Icon name="remove" />
                        <FormattedMessage
                            id="pages.schedule.CreateAppointmentDialog.cancel"
                            defaultMessage="Cancel"
                        />
                    </Button>
                </Modal.Actions>
            </Modal>
        </FormProvider>
    );
}

export default injectIntl(CreateAppointmentDialog);
